import type { AdminQuestClaim, QuestClaimStatus } from '@src/dto/QuestClaims';
import { useStore } from '@tanstack/react-store';
import type { SortingState } from '@tanstack/react-table';
import { ArrowLeft, Pencil, Plus } from 'lucide-react';
import {
  use,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { DataTable } from '../../components/common/DataTable';
import { FilterBar, SelectFilter } from '../../components/common/FilterBar';
import { Pagination } from '../../components/common/Pagination';
import { ManagementPageLayout } from '../../components/management/ManagementPageLayout';
import { Button } from '../../components/ui/button';
import { TRPCContext } from '../../contexts/TRPC';
import { useAsyncData } from '../../hooks/useAsyncData';
import { useManagementCrud } from '../../hooks/useManagementCrud';
import { usePageTitle } from '../../hooks/usePageTitle';
import type { QueryLoadContext } from '../../hooks/usePaginatedQueryLoader';
import { useRequireAuth } from '../../hooks/useRequireAuth';
import { formatSkrAmount, parseSkrUnitsToBaseUnits } from './skr';
import { isLeftResult, isRightResult } from '../../lib/trpcTypeGuards';
import {
  applyQuestClaimsUrlState,
  questClaimsStore,
  selectClaim,
  setClaims,
  setFilters,
  setLoading,
  setModalMode,
  setPage,
  setPaginationInfo,
  setSorting,
  updateFilter,
} from '../../stores/questClaimsStore';
import type { RightResult, RouterOutputs } from '../../types/TRPC';
import { captureAndReportError } from '../../utils/errorReporting';
import {
  buildQuestClaimsParams,
  parseQuestClaimsUrlState,
  QUEST_CLAIMS_URL_KEYS,
} from '../../utils/questClaimsUrlState';
import { canAccessAdminPages } from '../../utils/roleUtils';
import { formatDateTimeWithLocalZone } from '../../utils/stringHelpers';
import {
  mergeOwnedParams,
  ownedParamsSignature,
} from '../../utils/urlSearchParamSync';
import {
  type ClaimFormValues,
  type ClaimRoundOption,
  QuestClaimModal,
} from './QuestClaimModal';

type ClaimSortId =
  | 'created'
  | 'revealedAt'
  | 'amountBaseUnits'
  | 'status'
  | 'walletId';

const CLAIM_STATUS_BADGE_CLASSES: Record<QuestClaimStatus, string> = {
  Unrevealed: 'bg-neutral-500/10 text-neutral-400 border-neutral-500/20',
  Ready: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  Claimed: 'bg-green-500/10 text-green-400 border-green-500/20',
};

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'Unrevealed', label: 'Unrevealed' },
  { value: 'Ready', label: 'Ready' },
  { value: 'Claimed', label: 'Claimed' },
];

interface FilterData {
  seasons: { id: string; label: string }[];
  rounds: { id: string; seasonId: string; roundNumber: string }[];
  apps: {
    id: string;
    roundId: string;
    androidPackage: string;
    appName: string;
  }[];
}

const EMPTY_FILTER_DATA: FilterData = { seasons: [], rounds: [], apps: [] };

interface ClaimsLoadContext {
  filters: {
    seasonId: string;
    roundId: string;
    status: string;
    appAndroidPackage: string;
    search: string;
  };
  sorting: SortingState;
  pagination: {
    currentPage: number;
    totalPages: number;
    total: number;
  };
}

export function QuestClaimsPage() {
  const { authState, canRender } = useRequireAuth({ requireRole: 'admin' });
  const trpc = use(TRPCContext);
  usePageTitle('Quest Claims | Admin');

  const [searchParams, setSearchParams] = useSearchParams();
  const searchKey = searchParams.toString();
  // Set right before we write the URL ourselves so the hydrate effect that the
  // write triggers doesn't echo straight back into another write.
  const skipNextUrlWriteRef = useRef(false);

  const claims = useStore(questClaimsStore, (state) => state.claims);
  const selectedClaimId = useStore(
    questClaimsStore,
    (state) => state.ui.selectedClaimId,
  );
  const modalMode = useStore(questClaimsStore, (state) => state.ui.modalMode);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedClaim =
    claims.find((claim) => claim.id === selectedClaimId) ?? null;

  // URL → store. Runs on mount and on any URL change (incl. browser
  // back/forward). The store is a module-level singleton, so hydrating from the
  // URL also clears any stale filters/modal left over from a prior visit.
  // applyQuestClaimsUrlState is a no-op when nothing differs, so our own writes
  // don't loop.
  useLayoutEffect(() => {
    skipNextUrlWriteRef.current = true;
    applyQuestClaimsUrlState(
      parseQuestClaimsUrlState(new URLSearchParams(searchKey)),
    );
  }, [searchKey]);

  const { data: filterData, run: loadFilterData } = useAsyncData<FilterData>({
    initialData: EMPTY_FILTER_DATA,
  });

  useEffect(() => {
    if (!canRender) {
      return;
    }
    loadFilterData(() => trpc.client.getQuestWorkspace.query(), {
      onSuccess: (result, helpers) => {
        if (isRightResult(result)) {
          const workspace = result.right as RightResult<
            RouterOutputs['getQuestWorkspace']
          >;
          helpers.setData({
            seasons: workspace.seasons.map((season, index) => ({
              id: season.id,
              label: `Season ${index + 1}`,
            })),
            rounds: workspace.rounds.map((round) => ({
              id: round.id,
              seasonId: round.seasonId,
              roundNumber: round.roundNumber,
            })),
            apps: workspace.apps.map((app) => ({
              id: app.id,
              roundId: app.roundId,
              androidPackage: app.androidPackage,
              appName: app.appName,
            })),
          });
        } else if (isLeftResult(result)) {
          toast.error(result.left.message);
        }
      },
      onError: (error) => {
        captureAndReportError(error, {
          operation: 'admin.loadQuestWorkspace',
          component: 'QuestClaimsPage',
        });
        toast.error('Failed to load quest filters');
      },
    });
  }, [canRender, loadFilterData, trpc]);

  const loadClaims = async ({
    isCurrentRequest,
    filters,
    sorting,
    pagination,
  }: QueryLoadContext & ClaimsLoadContext) => {
    try {
      const result = await trpc.client.listQuestClaims.query({
        filters: {
          seasonId: filters.seasonId || undefined,
          roundId: filters.roundId || undefined,
          status:
            filters.status === 'all'
              ? undefined
              : (filters.status as QuestClaimStatus),
          appAndroidPackage: filters.appAndroidPackage || undefined,
          search: filters.search || undefined,
        },
        pagination: { page: pagination.currentPage, limit: 20 },
        sorting:
          sorting.length > 0
            ? sorting.map((sort) => ({
                id: sort.id as ClaimSortId,
                desc: sort.desc ?? false,
              }))
            : undefined,
      });

      if (!isCurrentRequest()) {
        return;
      }

      if (isRightResult(result)) {
        setClaims(result.right.claims);
        setPaginationInfo(
          pagination.currentPage,
          result.right.pagination.totalPages,
          result.right.pagination.total,
        );
      } else if (isLeftResult(result)) {
        toast.error(result.left.message);
      }
    } catch (error) {
      if (!isCurrentRequest()) {
        return;
      }
      captureAndReportError(error, {
        operation: 'admin.listQuestClaims',
        component: 'QuestClaimsPage',
        filters,
        page: pagination.currentPage,
      });
      toast.error('Failed to load claims');
    }
  };

  const { filters, sorting, pagination, isLoading, reload } = useManagementCrud(
    {
      enabled: canRender,
      store: questClaimsStore,
      actions: {
        updateFilter,
        setSorting,
        setPage,
        setPaginationInfo,
        setLoading,
      },
      loadPage: loadClaims,
    },
  );

  // store → URL. Mirrors filters/sorting/page/open-modal back into the query
  // string (replace, so it doesn't spam history) while preserving any params
  // this page doesn't own. Skips the write that the hydrate effect just queued.
  useEffect(() => {
    if (skipNextUrlWriteRef.current) {
      skipNextUrlWriteRef.current = false;
      return;
    }
    const desired = buildQuestClaimsParams({
      filters,
      sorting,
      page: pagination.currentPage,
      modalMode,
      selectedClaimId,
    });
    // Compare canonical-vs-canonical: the URL's owned params run back through
    // parse→build so a non-canonical-but-equivalent URL (an invalid value, an
    // explicit default, or an empty param) isn't seen as different from the
    // state we'd write, which would otherwise trigger a redundant write.
    const current = buildQuestClaimsParams(
      parseQuestClaimsUrlState(searchParams),
    );
    if (
      ownedParamsSignature(current, QUEST_CLAIMS_URL_KEYS) ===
      ownedParamsSignature(desired, QUEST_CLAIMS_URL_KEYS)
    ) {
      return;
    }
    setSearchParams(
      mergeOwnedParams(searchParams, QUEST_CLAIMS_URL_KEYS, desired),
      { replace: true },
    );
  }, [
    filters,
    sorting,
    pagination.currentPage,
    modalMode,
    selectedClaimId,
    searchParams,
    setSearchParams,
  ]);

  const seasonOptions = useMemo(
    () => [
      { value: '', label: 'All seasons' },
      ...filterData.seasons.map((season) => ({
        value: season.id,
        label: season.label,
      })),
    ],
    [filterData.seasons],
  );

  const seasonLabelById = useMemo(
    () =>
      new Map(filterData.seasons.map((season) => [season.id, season.label])),
    [filterData.seasons],
  );

  const roundOptions = useMemo(() => {
    const rounds = filters.seasonId
      ? filterData.rounds.filter((round) => round.seasonId === filters.seasonId)
      : filterData.rounds;
    return [
      { value: '', label: 'All rounds' },
      ...rounds.map((round) => ({
        value: round.id,
        label: `Round #${round.roundNumber}`,
      })),
    ];
  }, [filterData.rounds, filters.seasonId]);

  const appOptions = useMemo(() => {
    if (!filters.roundId) {
      return [{ value: '', label: 'Select a round first' }];
    }
    return [
      { value: '', label: 'All apps' },
      ...filterData.apps
        .filter((app) => app.roundId === filters.roundId)
        .map((app) => ({ value: app.androidPackage, label: app.appName })),
    ];
  }, [filterData.apps, filters.roundId]);

  const modalRoundOptions = useMemo<ClaimRoundOption[]>(() => {
    return filterData.rounds.map((round) => ({
      id: round.id,
      label: `${seasonLabelById.get(round.seasonId) ?? 'Season'} • Round #${round.roundNumber}`,
    }));
  }, [filterData.rounds, seasonLabelById]);

  const openAssign = () => {
    selectClaim(null);
    setModalMode('assign');
  };

  const openEdit = (claim: AdminQuestClaim) => {
    selectClaim(claim.id);
    setModalMode('edit');
  };

  const closeModal = () => {
    setModalMode('closed');
    selectClaim(null);
  };

  const handleSubmit = async (values: ClaimFormValues) => {
    const tierRank =
      values.tierRank.trim() === '' ? null : Number(values.tierRank);
    const amountBaseUnits = parseSkrUnitsToBaseUnits(values.amountSkr);
    if (amountBaseUnits === null || amountBaseUnits === '') {
      toast.error('Enter a valid SKR amount.');
      return;
    }

    setIsSubmitting(true);
    try {
      if (modalMode === 'assign') {
        const result = await trpc.client.assignQuestClaim.mutate({
          roundId: values.roundId,
          walletId: values.walletId.trim(),
          tierCode: values.tierCode.trim(),
          tierRank,
          amountBaseUnits,
          status: values.status,
        });
        if (isLeftResult(result)) {
          toast.error(result.left.message);
          return;
        }
        toast.success('Claim assigned');
      } else if (selectedClaim) {
        const result = await trpc.client.updateQuestClaim.mutate({
          id: selectedClaim.id,
          tierCode: values.tierCode.trim(),
          tierRank,
          amountBaseUnits,
          status: values.status,
          claimTransactionSignature:
            values.claimTransactionSignature.trim() === ''
              ? null
              : values.claimTransactionSignature.trim(),
        });
        if (isLeftResult(result)) {
          toast.error(result.left.message);
          return;
        }
        toast.success('Claim updated');
      } else {
        return;
      }

      closeModal();
      await reload();
    } catch (error) {
      captureAndReportError(error, {
        operation:
          modalMode === 'assign'
            ? 'admin.assignQuestClaim'
            : 'admin.updateQuestClaim',
        component: 'QuestClaimsPage',
      });
      toast.error('Failed to save claim');
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns = [
    {
      key: 'walletId',
      header: 'Wallet',
      size: 220,
      enableSorting: true,
      accessor: (claim: AdminQuestClaim) => (
        <span className="font-mono text-xs text-white break-all">
          {claim.walletId}
        </span>
      ),
    },
    {
      key: 'round',
      header: 'Season / Round',
      size: 220,
      enableSorting: false,
      accessor: (claim: AdminQuestClaim) => (
        <div className="space-y-0.5">
          <div className="text-sm text-white truncate">
            {seasonLabelById.get(claim.seasonId) ?? 'Season'}
          </div>
          <div className="text-xs text-neutral-400 truncate">
            Round #{claim.roundNumber}
          </div>
        </div>
      ),
    },
    {
      key: 'tier',
      header: 'Tier',
      size: 110,
      enableSorting: false,
      accessor: (claim: AdminQuestClaim) => (
        <div className="text-sm text-white">
          {claim.tierCode}
          {claim.tierRank !== null ? (
            <span className="text-neutral-500"> #{claim.tierRank}</span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'amountBaseUnits',
      header: 'Amount',
      size: 140,
      enableSorting: true,
      sortDescFirst: true,
      accessor: (claim: AdminQuestClaim) => (
        <span className="font-mono text-sm text-white">
          {formatSkrAmount(claim.amountBaseUnits)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      size: 130,
      enableSorting: true,
      accessor: (claim: AdminQuestClaim) => (
        <span
          className={`px-2 py-1 rounded text-xs font-medium border ${
            CLAIM_STATUS_BADGE_CLASSES[claim.status]
          }`}
        >
          {claim.status}
        </span>
      ),
    },
    {
      key: 'revealedAt',
      header: 'Ready since',
      size: 170,
      enableSorting: true,
      sortDescFirst: true,
      accessor: (claim: AdminQuestClaim) => (
        <span className="text-xs text-neutral-400">
          {claim.revealedAt
            ? formatDateTimeWithLocalZone(claim.revealedAt)
            : '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      size: 72,
      enableSorting: false,
      accessor: (claim: AdminQuestClaim) => (
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openEdit(claim);
            }}
            className="text-teal-400 transition-colors hover:text-teal-300"
            title="Edit claim"
          >
            <Pencil className="size-4" />
          </button>
        </div>
      ),
    },
  ];

  if (!canRender) {
    return null;
  }

  return (
    <>
      <ManagementPageLayout
        authState={authState}
        canAccess={canAccessAdminPages}
      >
        <div className="space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-2">
              <Link
                to="/quests"
                className="inline-flex items-center gap-1.5 text-xs text-neutral-400 transition-colors hover:text-white"
              >
                <ArrowLeft className="size-3.5" />
                Back to quests
              </Link>
              <h1 className="text-2xl font-semibold text-white">
                Quest Claims
              </h1>
              <p className="text-sm text-neutral-400">
                Review and manage per-wallet allocations across seasons and
                rounds.
              </p>
            </div>
            <Button
              type="button"
              onClick={openAssign}
              className="bg-teal-600 text-white hover:bg-teal-700"
            >
              <Plus className="size-4" />
              Assign claim
            </Button>
          </div>

          <FilterBar
            searchValue={filters.search}
            onSearchChange={(value) => updateFilter('search', value)}
            searchPlaceholder="Search by wallet…"
          >
            <SelectFilter
              value={filters.seasonId}
              onChange={(value) =>
                setFilters({
                  seasonId: value,
                  roundId: '',
                  appAndroidPackage: '',
                })
              }
              options={seasonOptions}
            />
            <SelectFilter
              value={filters.roundId}
              onChange={(value) =>
                setFilters({ roundId: value, appAndroidPackage: '' })
              }
              options={roundOptions}
            />
            <SelectFilter
              value={filters.appAndroidPackage}
              onChange={(value) => updateFilter('appAndroidPackage', value)}
              options={appOptions}
            />
            <SelectFilter
              value={filters.status}
              onChange={(value) => updateFilter('status', value)}
              options={STATUS_FILTER_OPTIONS}
            />
          </FilterBar>

          <div className="bg-neutral-800/50 rounded-lg border border-neutral-700/50 overflow-hidden shadow-lg">
            <div className="px-6 py-4 border-b border-neutral-700/50">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <h3 className="text-base font-semibold text-white">
                    {pagination.total}{' '}
                    {pagination.total === 1 ? 'Claim' : 'Claims'}
                  </h3>
                  <p className="text-xs text-neutral-400">
                    One allocation per wallet per round.
                  </p>
                </div>
                {pagination.totalPages > 1 && (
                  <div className="text-xs text-neutral-500">
                    Page {pagination.currentPage} of {pagination.totalPages}
                  </div>
                )}
              </div>
            </div>

            <DataTable
              data={claims}
              columns={columns}
              isLoading={isLoading}
              emptyMessage="No claims found"
              enableSorting={true}
              sorting={sorting}
              onSortingChange={(updater) => setSorting(updater)}
              getRowId={(claim) => claim.id}
              renderMobileItem={(claim) => (
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <span className="font-mono text-xs text-white break-all">
                      {claim.walletId}
                    </span>
                    <span
                      className={`shrink-0 px-2 py-1 rounded text-xs font-medium border ${
                        CLAIM_STATUS_BADGE_CLASSES[claim.status]
                      }`}
                    >
                      {claim.status}
                    </span>
                  </div>
                  <div className="text-xs text-neutral-400">
                    {seasonLabelById.get(claim.seasonId) ?? 'Season'} • Round #
                    {claim.roundNumber}
                  </div>
                  <div className="font-mono text-sm text-white">
                    {formatSkrAmount(claim.amountBaseUnits)}
                  </div>
                  <div className="flex justify-end gap-4 border-t border-neutral-700/50 pt-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEdit(claim);
                      }}
                      className="flex items-center gap-1 text-xs font-medium text-teal-400 hover:text-teal-300"
                    >
                      <Pencil className="size-3.5" />
                      Edit
                    </button>
                  </div>
                </div>
              )}
            />

            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={setPage}
            />
          </div>
        </div>
      </ManagementPageLayout>

      <QuestClaimModal
        isOpen={
          modalMode === 'assign' ||
          (modalMode === 'edit' && selectedClaim !== null)
        }
        mode={modalMode === 'edit' ? 'edit' : 'assign'}
        claim={selectedClaim}
        rounds={modalRoundOptions}
        isSubmitting={isSubmitting}
        onClose={closeModal}
        onSubmit={handleSubmit}
      />
    </>
  );
}
